import { PriceList } from "@medusajs/medusa"
import clsx from "clsx"
import { useAdminCustomerGroups } from "medusa-react"
import React, { useState } from "react"
import { Controller } from "react-hook-form"
import DatePicker from "../../../../components/atoms/date-picker/date-picker"
import TimePicker from "../../../../components/atoms/date-picker/time-picker"
import Switch from "../../../../components/atoms/switch"
import Select from "../../../../components/molecules/select"
import Accordion from "../../../../components/organisms/accordion"
import { usePriceListForm } from "../form/pricing-form-context"
import { ConfigurationField } from "../types"

type ConfigurationProps = {
  priceList?: PriceList
}

const checkForEnabledConfigs = (
  config: Record<ConfigurationField, unknown>
): string[] => {
  const enabledConfigs: string[] = []

  if (config.customer_groups?.length > 0) {
    enabledConfigs.push(ConfigurationField.CUSTOMER_GROUPS)
  }
  if (config.starts_at) {
    enabledConfigs.push(ConfigurationField.STARTS_AT)
  }
  if (config.ends_at) {
    enabledConfigs.push(ConfigurationField.ENDS_AT)
  }

  return enabledConfigs
}

const Configuration: React.FC<ConfigurationProps> = () => {
  const { customer_groups, isLoading } = useAdminCustomerGroups()
  const {
    control,
    handleConfigurationSwitch,
    configFields,
  } = usePriceListForm()
  const [openItems, setOpenItems] = useState<string[]>(
    checkForEnabledConfigs(configFields)
  )

  return (
    <Accordion.Item
      forceMountContent
      title="Configuration"
      tooltip="Optional configuration for the price list"
      value="configuration"
      description="The price overrides apply from the time you hit the publish button and forever if left untouched."
    >
      <Accordion
        type="multiple"
        value={openItems}
        onValueChange={(values) => {
          handleConfigurationSwitch(values)
          setOpenItems(values)
        }}
      >
        <div className="mt-5">
          <Accordion.Item
            headingSize="medium"
            forceMountContent
            className="border-b-0"
            title="Price overrides has a start date?"
            subtitle="Schedule the price overrides to activate in the future."
            value="starts_at"
            customTrigger={
              <Switch checked={openItems.indexOf("starts_at") > -1} />
            }
          >
            <div
              className={clsx(
                "flex items-center gap-xsmall accordion-margin-transition",
                {
                  "mt-4": openItems.indexOf("starts_at") > -1,
                }
              )}
            >
              <Controller
                name="starts_at"
                control={control}
                render={({ value, onChange }) => {
                  return (
                    <>
                      <DatePicker
                        date={value}
                        label="Start date"
                        onSubmitDate={onChange}
                      />
                      <TimePicker
                        date={value}
                        label="Start date"
                        onSubmitDate={onChange}
                      />
                    </>
                  )
                }}
              />
            </div>
          </Accordion.Item>
          <Accordion.Item
            headingSize="medium"
            forceMountContent
            className="border-b-0"
            title="Price overrides has an expiry date?"
            subtitle="Schedule the price overrides to deactivate in the future."
            value="ends_at"
            customTrigger={
              <Switch checked={openItems.indexOf("ends_at") > -1} />
            }
          >
            <div
              className={clsx(
                "flex items-center gap-xsmall accordion-margin-transition",
                {
                  "mt-4": openItems.indexOf("ends_at") > -1,
                }
              )}
            >
              <Controller
                name="ends_at"
                control={control}
                render={({ value, onChange }) => {
                  return (
                    <>
                      <DatePicker
                        date={value}
                        label="End date"
                        onSubmitDate={onChange}
                      />
                      <TimePicker
                        date={value}
                        label="End date"
                        onSubmitDate={onChange}
                      />
                    </>
                  )
                }}
              />
            </div>
          </Accordion.Item>
          <Accordion.Item
            headingSize="medium"
            forceMountContent
            className="border-b-0"
            title="Customer availabilty"
            subtitle="Specifiy which customer groups the price overrides should apply for."
            value="customer_groups"
            customTrigger={
              <Switch checked={openItems.indexOf("customer_groups") > -1} />
            }
          >
            <Controller
              name="customer_groups"
              control={control}
              css={{ width: "100%" }}
              render={({ value, onChange, ref }) => {
                return (
                  <div
                    className={clsx(
                      "flex items-center gap-xsmall accordion-margin-transition w-full",
                      {
                        "mt-4": openItems.indexOf("customer_groups") > -1,
                      }
                    )}
                  >
                    <Select
                      value={value}
                      label="Customer Groups"
                      onChange={onChange}
                      isMultiSelect
                      fullWidth
                      enableSearch
                      hasSelectAll
                      isLoading={isLoading}
                      options={
                        customer_groups?.map((cg) => ({
                          label: cg.name,
                          value: cg.id,
                        })) || []
                      }
                      ref={ref}
                    />
                  </div>
                )
              }}
            />
          </Accordion.Item>
        </div>
      </Accordion>
    </Accordion.Item>
  )
}

export default Configuration
